import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, Table, Select, Space, Tag, Button, Tooltip, Switch, Typography, message } from "antd";
import { DesktopOutlined, MobileOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "../../../config/axiosInstance";
import dayjs from "dayjs";

const { Option } = Select;
const { Text } = Typography;

const monthNames = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
];

const DeviceUsageMonthsFlat = ({ client, year, darkTheme }) => {
    const [loading, setLoading] = useState(false);
    const [billingLoading, setBillingLoading] = useState(false);
    const [monthlyData, setMonthlyData] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [showBilled, setShowBilled] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(undefined);
    const [selectedConfig, setSelectedConfig] = useState(undefined);

    const fetchMonthlyUsage = useCallback(async () => {
        if (!client?._id || !year) return;

        try {
            setLoading(true);

            const params = {};
            if (showBilled) {
                params.billed = true;
            }

            const response = await axios.get(
                `/clients-report/${client._id}/device-usage-months/${year}`,
                { params }
            );

            const data = Array.isArray(response.data) ? response.data : [];
            setMonthlyData(data);

            if (data.length > 0) {
                const monthExists = data.some(item => item.month === selectedMonth);
                if (!selectedMonth || !monthExists) {
                    setSelectedMonth(data[0].month);
                }
            } else {
                setSelectedMonth(null);
            }
        } catch (error) {
            console.error("Error fetching monthly device usage:", error);
            message.error("Error loading monthly device usage");
        } finally {
            setLoading(false);
        }
    }, [client?._id, year, showBilled, selectedMonth]);

    useEffect(() => {
        fetchMonthlyUsage();
    }, [fetchMonthlyUsage]);

    const availableMonths = useMemo(() => {
        return monthlyData.map(item => ({
            value: item.month,
            label: monthNames.find(m => m.value === item.month)?.label || item.monthKey
        }));
    }, [monthlyData]);

    const selectedMonthData = useMemo(() => {
        return monthlyData.find(item => item.month === selectedMonth) || null;
    }, [monthlyData, selectedMonth]);

    const flatRows = useMemo(() => {
        if (!selectedMonthData?.events) return [];

        return selectedMonthData.events.flatMap(event =>
            (event.devices || []).flatMap(device =>
                (device.configs || []).map((config, index) => ({
                    key: config.logId || `${event.eventID}-${device.deviceID}-${index}`,
                    logId: config.logId,
                    deviceID: device.deviceID,
                    serialNumber: device.serialNumber,
                    deviceName: device.name,
                    type: device.type,
                    configName: config.name || "-",
                    eventID: event.eventID,
                    eventMnemonic: event.event_mnemonic,
                    eventName: event.event_name,
                    usedOn: config.usedOn,
                    usedOnDay: config.usedOn ? dayjs(config.usedOn).format("YYYY-MM-DD") : null,
                    billable: config.billable,
                    billed: config.billed,
                    billedAt: config.billedAt,
                    selectedProgrammePointId: config.selectedProgrammePointId,
                    selectedProgrammePointName: config.selectedProgrammePointName,
                    selectedLocationId: config.selectedLocationId
                }))
            )
        );
    }, [selectedMonthData]);

    const eventOptions = useMemo(() => {
        const map = new Map();

        flatRows.forEach(row => {
            if (!map.has(row.eventID)) {
                map.set(row.eventID, {
                    value: row.eventID,
                    label: `${row.eventMnemonic}${row.eventName ? ` - ${row.eventName}` : ""}`
                });
            }
        });

        return Array.from(map.values());
    }, [flatRows]);

    const configOptions = useMemo(() => {
        const map = new Map();

        flatRows.forEach(row => {
            const key = row.configName || "-";
            if (!map.has(key)) {
                map.set(key, {
                    value: key,
                    label: key
                });
            }
        });

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [flatRows]);

    const filteredRows = useMemo(() => {
        return flatRows.filter(row => {
            const matchesEvent = !selectedEvent || row.eventID === selectedEvent;
            const matchesConfig = !selectedConfig || row.configName === selectedConfig;
            return matchesEvent && matchesConfig;
        });
    }, [flatRows, selectedEvent, selectedConfig]);

    const isRowBillable = (row) => {
        const val = typeof row.billable === "string" ? row.billable.toLowerCase() : row.billable;
        return !(val === "not billable" || val === 0);
    };

    const getRelatedLogIds = (row) => {
        if (!row.deviceID || !row.eventID || !row.usedOnDay) {
            return row.logId ? [row.logId] : [];
        }

        const ids = flatRows
            .filter(item =>
                item.deviceID === row.deviceID &&
                item.eventID === row.eventID &&
                item.usedOnDay === row.usedOnDay
            )
            .map(item => item.logId)
            .filter(Boolean);

        return [...new Set(ids)];
    };

    const handleMarkRowAsBilled = async (row, billedValue = true) => {
        const logIds = getRelatedLogIds(row);

        if (!logIds.length) {
            message.warning("This row has no related logIds");
            return;
        }

        try {
            setBillingLoading(true);

            await axios.patch(`/clients-report/${client._id}/device-usage-months/mark-billed`, {
                logIds,
                billed: billedValue
            });

            message.success(billedValue ? "Usage marked as billed" : "Usage marked as pending");
            await fetchMonthlyUsage();
        } catch (error) {
            console.error("Error updating billing status:", error);
            message.error("Error updating billing status");
        } finally {
            setBillingLoading(false);
        }
    };

    const columns = [
        {
            title: "Device Name",
            dataIndex: "deviceName",
            key: "deviceName",
            width: 200,
            render: (text, record) => (
                <Tooltip title={`Serial Number: ${record.serialNumber || "-"}`}>
                    <span>{text || "-"}</span>
                </Tooltip>
            )
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 60,
            align: "center",
            render: (type) => {
                const icon = type === "mobile"
                    ? <MobileOutlined style={{ color: "#52c41a", fontSize: "18px" }} />
                    : <DesktopOutlined style={{ color: "#1677ff", fontSize: "18px" }} />;

                return icon;
            }
        },
        {
            title: "Config Name",
            dataIndex: "configName",
            key: "configName",
            width: 200
        },
        {
            title: "Event",
            dataIndex: "eventMnemonic",
            key: "eventMnemonic",
            width: 120,
            render: (text, record) => (
                <Tooltip title={record.eventName}>
                    <span>{text}</span>
                </Tooltip>
            )
        },
        {
            title: "Used On",
            dataIndex: "usedOn",
            key: "usedOn",
            width: 110,
            render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-")
        },
        {
            title: "Billable",
            dataIndex: "billable",
            key: "billable",
            width: 100,
            render: (value) => {
                const val = typeof value === "string" ? value.toLowerCase() : value;
                if (val === "not billable" || val === 0) {
                    return <Tag color="red">NOT BILLABLE</Tag>;
                }
                return <Tag color="green">BILLABLE</Tag>;
            }
        },
        {
            title: "Billing Status",
            dataIndex: "billed",
            key: "billed",
            width: 100,
            render: (billed, row) => {
                if (billed) {
                    return (
                        <Tooltip title={row.billedAt ? dayjs(row.billedAt).format("DD-MM-YYYY HH:mm") : ""}>
                            <Tag color="blue">BILLED</Tag>
                        </Tooltip>
                    );
                }

                return <Tag color="orange">PENDING</Tag>;
            }
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            align: "center",
            render: (_, row) => {
                const billable = isRowBillable(row);

                if (!billable) {
                    return null;
                }

                const isBilled = !!row.billed;

                return (
                    <Button
                        type={isBilled ? "default" : "primary"}
                        danger={isBilled}
                        size="small"
                        loading={billingLoading}
                        onClick={() => handleMarkRowAsBilled(row, !isBilled)}
                    >
                        {isBilled ? "Undo" : "Billed"}
                    </Button>
                );
            }
        }
    ];

    return (
        <Card
            title="Device Usage by Month"
            style={{ marginTop: 20 }}
            extra={
                <Space wrap>
                    <Select
                        value={selectedMonth}
                        onChange={(value) => {
                            setSelectedMonth(value);
                            setSelectedEvent(undefined);
                            setSelectedConfig(undefined);
                        }}
                        style={{ width: 180 }}
                        placeholder="Select month"
                        allowClear
                    >
                        {availableMonths.map(month => (
                            <Option key={month.value} value={month.value}>
                                {month.label}
                            </Option>
                        ))}
                    </Select>

                    <Select
                        value={selectedEvent}
                        onChange={setSelectedEvent}
                        style={{ width: 240 }}
                        placeholder="Filter by event"
                        allowClear
                    >
                        {eventOptions.map(item => (
                            <Option key={item.value} value={item.value}>
                                {item.label}
                            </Option>
                        ))}
                    </Select>

                    <Select
                        value={selectedConfig}
                        onChange={setSelectedConfig}
                        style={{ width: 240 }}
                        placeholder="Filter by config"
                        allowClear
                    >
                        {configOptions.map(item => (
                            <Option key={item.value} value={item.value}>
                                {item.label}
                            </Option>
                        ))}
                    </Select>

                    <Space size="small">
                        <Text>Show billed</Text>
                        <Switch checked={showBilled} onChange={setShowBilled} />
                    </Space>

                    <Button icon={<ReloadOutlined />} onClick={fetchMonthlyUsage}>
                        Refresh
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={filteredRows}
                rowKey="key"
                loading={loading}
                scroll={{ x: "max-content" }}
                bordered
                locale={{
                    emptyText: selectedMonth
                        ? "No device usage found for the selected filters"
                        : "No monthly data available"
                }}
                summary={() => {
                    if (!selectedMonthData) return null;

                    const billableCount = filteredRows.filter(row => isRowBillable(row)).length;

                    return (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={4}>
                                <strong>
                                    {monthNames.find(m => m.value === selectedMonthData.month)?.label || selectedMonthData.monthKey}
                                </strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="center">
                                <strong>{filteredRows.length}</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="center">
                                <strong>{billableCount}</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} colSpan={2} />
                        </Table.Summary.Row>
                    );
                }}
            />
        </Card>
    );
};

export default DeviceUsageMonthsFlat;